/**
 * Provider Details Sections Configuration
 * 
 * Centralized configuration for provider detail page sections.
 * Each section declares:
 * - id: unique identifier for tabs
 * - label: display name
 * - hasData: validation function to determine if section should render
 * 
 * Benefits:
 * - Single source of truth for section structure
 * - Easy to add/remove/reorder sections
 * - Parent components don't need section-specific logic
 * - Better testability and maintainability
 */

import type { ProviderProfile } from '../types/provider-profile.types';
import {
  hasOverviewData,
  hasServicesData,
  hasQualificationsData,
  hasReviewsData,
} from './fieldValidation';

export type ProviderSection = {
  /** Unique identifier for the section/tab */
  id: string;
  
  /** Display label for the tab */
  label: string;
  
  /** Function to determine if section has data to display */
  hasData: (provider: ProviderProfile, additionalData?: Record<string, unknown>) => boolean;
  
  /** Order for display (lower numbers appear first) */
  order: number;
};

/**
 * Get available sections for provider details page
 * Sections are returned in display order
 * 
 * @param provider - Provider profile data
 * @param options - Additional data needed for validation (services, qualifications, media, reviews)
 * @returns Array of section configurations that have data
 */
export const getProviderDetailsSections = (
  provider: ProviderProfile | null | undefined,
  options?: {
    servicesData?: { services: Array<{ id: string }> } | null;
    qualificationsData?: {
      badges?: Array<{ badgeName: string }>;
      expertise?: Array<{ id: string }>;
      specialties?: Array<{ id: string }>;
    } | null;
    mediaData?: { media: Array<{ id: string }> } | null;
    reviewsData?: {
      googleReviews?: { reviews?: unknown[] } | null;
      averageRating?: number | null;
    } | null;
    resourcesData?: Array<{ id: string }> | null;
  }
): ProviderSection[] => {
  if (!provider) {
    // During loading, show all sections (they'll show loading states)
    return getAllSections();
  }

  const sections: ProviderSection[] = [
    {
      id: 'overview',
      label: 'Overview',
      hasData: (p) => hasOverviewData(p),
      order: 1,
    },
    {
      id: 'services',
      label: 'Services',
      hasData: () => {
        if (!options?.servicesData) return true; // Show while loading
        return hasServicesData({ services: options.servicesData.services });
      },
      order: 2,
    },
    {
      id: 'care-delivery',
      label: 'Care Delivery',
      hasData: (p) => {
        // Feature 038: Show Care Delivery tab if provider has any care delivery information
        return Boolean(
          p.priceDetailsText ||
          p.carePhilosophy ||
          p.emergencyResponseDetails
        );
      },
      order: 2.5,
    },
    {
      id: 'qualifications',
      label: 'Qualifications',
      hasData: (p) => {
        if (!options?.qualificationsData) return true; // Show while loading
        return hasQualificationsData({
          badges: options.qualificationsData.badges,
          expertise: options.qualificationsData.expertise,
          specialties: options.qualificationsData.specialties,
          awardsRecognitions: p.awardsRecognitions,
          professionalAssociations: p.professionalAssociations,
          certificationsArray: p.certificationsArray,
          professionalLicenses: p.professionalLicenses,
        });
      },
      order: 3,
    },
    {
      id: 'reviews',
      label: 'Reviews',
      hasData: (p) => {
        if (!options?.reviewsData) return true; // Show while loading
        return hasReviewsData({
          placeId: p.placeId,
          googleReviews: options.reviewsData.googleReviews,
          averageRating: p.averageRating,
        });
      },
      order: 5,
    },
    {
      id: 'resources',
      label: 'Resources',
      hasData: (p) => {
        // Feature 011 - US1: Show Resources tab if provider has authored resources OR photos
        // Feature 073: ...OR has at least one live PDF document
        if (!options?.mediaData && !options?.resourcesData) return true; // Show while loading
        const hasPhotos = Boolean(options?.mediaData?.media && options.mediaData.media.length > 0);
        const hasResources = Boolean(options?.resourcesData && options.resourcesData.length > 0);
        const hasDocuments = Boolean(p.documents && p.documents.length > 0);
        return hasPhotos || hasResources || hasDocuments;
      },
      order: 4,
    },
  ];

  // Filter sections that have data and sort by order
  return sections
    .filter(section => section.hasData(provider, options))
    .sort((a, b) => a.order - b.order);
};

/**
 * Get all sections regardless of data availability
 * Used during loading states
 */
export const getAllSections = (): ProviderSection[] => [
  { id: 'overview', label: 'Overview', hasData: () => true, order: 1 },
  { id: 'services', label: 'Services', hasData: () => true, order: 2 },
  { id: 'care-delivery', label: 'Care Delivery', hasData: () => true, order: 2.5 },
  { id: 'qualifications', label: 'Qualifications', hasData: () => true, order: 3 },
  { id: 'resources', label: 'Resources', hasData: () => true, order: 4 },
  { id: 'reviews', label: 'Reviews', hasData: () => true, order: 5 },
];

/**
 * Get the default active tab (first available section)
 */
export const getDefaultTab = (sections: ProviderSection[]): string => {
  return sections[0]?.id || 'overview';
};

